import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const Contact = () => {
  const contactRef = useRef(null);
  const emailButtonRef = useRef(null);
  const whatsappRef = useRef(null);
  const footerRef = useRef(null);

  useEffect(() => {
    const contactSection = contactRef.current;
    const emailButton = emailButtonRef.current;
    const whatsapp = whatsappRef.current;
    const footer = footerRef.current;

    // Initial setup - hide elements
    gsap.set([emailButton, whatsapp, footer], {
      opacity: 0,
      y: 50,
    });

    // Scroll trigger animation
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: contactSection,
        start: "top 80%",
        end: "bottom 20%",
        toggleActions: "play none none reverse",
      },
    });

    tl.to(emailButton, {
      opacity: 1,
      y: 0,
      duration: 0.8,
      ease: "power2.out",
    })
      .to(
        whatsapp,
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: "power2.out",
        },
        "-=0.4"
      )
      .to(
        footer,
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: "power2.out",
        },
        "-=0.3"
      );

    // Email button hover animation
    const handleEmailHover = () => {
      gsap.to(emailButton, {
        scale: 1.05,
        duration: 0.3,
        ease: "power2.out",
      });
    };

    const handleEmailLeave = () => {
      gsap.to(emailButton, {
        scale: 1,
        duration: 0.3,
        ease: "power2.out",
      });
    };

    // WhatsApp button hover animation
    const handleWhatsappHover = () => {
      gsap.to(whatsapp, {
        scale: 1.03,
        duration: 0.3,
        ease: "power2.out",
      });
    };

    const handleWhatsappLeave = () => {
      gsap.to(whatsapp, {
        scale: 1,
        duration: 0.3,
        ease: "power2.out",
      });
    };

    // Add event listeners
    if (emailButton) {
      emailButton.addEventListener("mouseenter", handleEmailHover);
      emailButton.addEventListener("mouseleave", handleEmailLeave);
    }

    if (whatsapp) {
      whatsapp.addEventListener("mouseenter", handleWhatsappHover);
      whatsapp.addEventListener("mouseleave", handleWhatsappLeave);
    }

    // Attention-drawing animation for email button
    const attentionAnimation = gsap.timeline({ repeat: -1, delay: 2 });
    attentionAnimation
      .to(emailButton, {
        boxShadow:
          "0 0 30px rgba(255, 0, 204, 0.6), 0 0 60px rgba(51, 51, 255, 0.4)",
        duration: 1.5,
        ease: "power2.inOut",
      })
      .to(emailButton, {
        boxShadow:
          "0 0 15px rgba(255, 0, 204, 0.3), 0 0 30px rgba(51, 51, 255, 0.2)",
        duration: 1.5,
        ease: "power2.inOut",
      });

    // Cleanup
    return () => {
      if (emailButton) {
        emailButton.removeEventListener("mouseenter", handleEmailHover);
        emailButton.removeEventListener("mouseleave", handleEmailLeave);
      }
      if (whatsapp) {
        whatsapp.removeEventListener("mouseenter", handleWhatsappHover);
        whatsapp.removeEventListener("mouseleave", handleWhatsappLeave);
      }
      attentionAnimation.kill();
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  return (
    <section className="section contact-section" ref={contactRef}>
      <div className="contact-container">
        <div className="contact-content">
          <h1 className="contact-title">Let's Talk AI & Ideas</h1>
          <div className="contact-buttons">
            <a
              href="mailto:hello@atomik.bn"
              className="email-button"
              ref={emailButtonRef}
            >
              hello@atomik.bn
            </a>

            <a
              href="https://wa.me/6738969341"
              target="_blank"
              rel="noopener noreferrer"
              className="whatsapp-button"
              ref={whatsappRef}
            >
              <div className="whatsapp-content">
                <svg
                  className="whatsapp-icon"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.893 3.386" />
                </svg>
                <span></span>
              </div>
            </a>
          </div>
        </div>

        <footer className="contact-footer" ref={footerRef}>
          <div className="footer-content">
            <div className="footer-address">
              <p>
                Unit 9, Level 1, Little Soho, Batu Bersurat, Brunei Darussalam
                BE3719
              </p>
            </div>

            <div className="footer-info">
              <p className="made-in-brunei">Built in Brunei ♥️</p>
              <p className="fka-text">FKA Think Axis Solutions</p>
            </div>
          </div>
        </footer>
      </div>
    </section>
  );
};

export default Contact;
